import { findRfiCatalogEntry } from "./serviceCategoriesColors";
import { getRfiCapabilityBadgeDisplayName } from "./rfiCatalogServiceNames";

function firstNonEmptyArray(...candidates) {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) return candidate;
  }
  return [];
}

function capabilityId(cap) {
  if (cap == null || typeof cap === "string") return "";
  const raw = cap.rfi_id ?? cap.rfiId ?? cap.value;
  return raw != null && raw !== "" ? String(raw) : "";
}

function catalogLookupInput(cap) {
  if (cap == null || typeof cap === "string") {
    return cap;
  }
  return {
    ...cap,
    rfi_id: cap.rfi_id ?? cap.rfiId ?? cap.value,
    rfi_name: cap.rfi_name ?? cap.rfiName ?? cap.label ?? cap.name,
  };
}

/** Display name on list/Basic-Profile/document capability rows. */
export function getCapabilityDisplayName(cap, rfisList = []) {
  if (cap == null) return "";
  if (typeof cap === "string") return cap.trim();
  const direct = (
    cap.rfi_name ||
    cap.rfiName ||
    cap.name ||
    cap.label ||
    ""
  ).trim();
  if (direct) return direct;
  if (!rfisList?.length) return "";
  return (findRfiCatalogEntry(catalogLookupInput(cap), rfisList)?.rfi_name || "").trim();
}

/** Capabilities from list provider (provider_capabilities) or Basic-Profile (capabilities). */
export function getProviderCapabilitiesList(source) {
  if (!source) return [];
  return firstNonEmptyArray(
    source.provider_capabilities,
    source.providerCapabilities,
    source.capabilities,
  );
}

function documentRows(documents) {
  if (!documents) return [];
  if (Array.isArray(documents)) return documents;
  return (
    documents.items ||
    documents.document_request_for_provider?.items ||
    documents.document_request_for_sponsor?.items ||
    []
  );
}

/** Unique capabilities shown on Library Documents cards (same `capabilities` array). */
export function collectUniqueDocumentCapabilities(documents) {
  const seen = new Set();
  const ordered = [];
  documentRows(documents).forEach((doc) => {
    const caps = doc?.capabilities || doc?.services || [];
    caps.forEach((cap) => {
      const id = capabilityId(cap);
      const nameKey = getCapabilityDisplayName(cap).toLowerCase();
      const key = id || nameKey;
      if (!key || seen.has(key)) return;
      seen.add(key);
      ordered.push(cap);
    });
  });
  return ordered;
}

function mergeCapabilityLists(primary = [], secondary = []) {
  const seen = new Set();
  const merged = [];
  [...primary, ...secondary].forEach((cap) => {
    const id = capabilityId(cap);
    const nameKey = getCapabilityDisplayName(cap).toLowerCase();
    const key = id || nameKey;
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(cap);
  });
  return merged;
}

/**
 * Service categories for the vendor profile, aligned with Library Documents cards:
 * profile capabilities first, then unique document-card capabilities so the
 * two surfaces show the same services/names.
 */
export function resolveVendorProfileServiceCategories({
  profile,
  documents,
  rfisList = [],
} = {}) {
  const merged = mergeCapabilityLists(
    getProviderCapabilitiesList(profile),
    collectUniqueDocumentCapabilities(documents),
  );
  const seenNames = new Set();
  const ordered = [];
  merged.forEach((cap) => {
    const raw = getCapabilityDisplayName(cap, rfisList);
    if (!raw) return;
    const name = getRfiCapabilityBadgeDisplayName(raw) || raw;
    if (!name || seenNames.has(name)) return;
    seenNames.add(name);
    ordered.push({
      name,
      capability: {
        ...cap,
        rfi_id: capabilityId(cap) || cap.rfi_id,
        rfi_name: name,
      },
    });
  });
  return ordered;
}

/** Verbatim service category names from the vendor profile capabilities. */
export function getVendorListedServiceCategories(provider, rfisList = []) {
  return resolveVendorProfileServiceCategories({
    profile: provider,
    rfisList,
  });
}

function optionKey(opt) {
  const id = opt?.value != null && opt.value !== "" ? String(opt.value) : "";
  const name = (opt?.label || "").toString().trim().toLowerCase();
  return id || name;
}

function toServiceCategoryOption(cap, rfisList = []) {
  const raw = getCapabilityDisplayName(cap, rfisList);
  const name = getRfiCapabilityBadgeDisplayName(raw) || raw;
  return {
    value: capabilityId(cap) || cap.value,
    label: name,
  };
}

/** Unique Library Documents services as form options `{ value, label }`. */
export function getLibraryDocumentServiceOptions(documents, rfisList = []) {
  return collectUniqueDocumentCapabilities(documents)
    .map((cap) => toServiceCategoryOption(cap, rfisList))
    .filter((opt) => (opt.value != null && opt.value !== "") || opt.label);
}

/**
 * Keep Library Documents services in the selected list so they stay visible
 * in the form even when they are not in provider_capabilities.
 */
export function mergeLibraryDocumentServiceOptions(
  selected = [],
  documents,
  rfisList = []
) {
  const current = Array.isArray(selected) ? selected : [];
  const extra = [];
  getLibraryDocumentServiceOptions(documents, rfisList).forEach((opt) => {
    const name = (opt.label || "").toLowerCase();
    const already = current.some(
      (item) =>
        (opt.value != null &&
          opt.value !== "" &&
          String(item?.value) === String(opt.value)) ||
        (name && (item?.label || "").toLowerCase() === name) ||
        optionKey(item) === optionKey(opt)
    );
    if (!already) extra.push(opt);
  });
  return extra.length ? [...current, ...extra] : current;
}

